export const meta = {
  id: '03-olla',
  order: 3,
  checkFocus: { tense: ['present'] },   // what the end-of-lesson check tests first
  titleFi: 'Olla-verbi',
  titleEn: 'The verb ‘to be’',
  // the lesson's own words; the plan adds the week's vocabulary quota (docs/COURSE.md)
  lemmaIds: ['kirine'],
  exampleIds: [
    'pt:cm-c-0020',
    'hotd-s3e08-008',
    'pt:cm-c-0022',
    'got-s3e06-004',
    'got-s5e04-014',
    'pt:cm-c-0032',
    'ex-c9d2a2dffa65',
    'pt:cm-c-0029',
    'ex-3155ea94aecf',
    'pt:cm-c-0035',
    'got-s3e08-002',
    'got-s3e04-006',
    'hotd-s1e05-003',
    'got-s7e07-005',
    'pt:pt-c0001',
    // ex-1fc98cacdc51 and ex-f000dbb85f4e (ilagon, olla jossakin) are shown as a preview of the locative
    // lesson only: listed, their ilan / ilā would take the check's present-tense slots meant for sagon
    'ex-238a7b80dcce',
    'hotd-s1e01-021',
    'ex-083c392857e8',
    'ex-677f3283fdca',
    'got-s5e04-003',
    'got-s5e03-004',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Sagon',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Basics_1',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Phrases_1',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Locative',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Subjunctive',
    'https://wiki.languageinvention.com/index.php?title=Ilagon',
    'https://wiki.languageinvention.com/index.php?title=Jagon',
    'https://github.com/svetlyobg/duolingo-high-valyrian/blob/main/duolingo-high-valyrian.md',
    'https://dedalvs.tumblr.com/post/48687381103/hi-there-i-really-love-the-phrases-valar',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['olla'],
  grammarAlso: ['present'],
  showLines: ['got-s3e06-004', 'got-s3e08-002', 'got-s5e04-014', 'got-s7e07-005'],
  // words: headwords only (Puhu records a dictionary word): lesson 1's dāez and īlva and the week's own long
  // vowels, stressed and not (vīlībagon); iksā, iksāt and iksan are in the text's Pronounce boxes, and iksā
  // reaches Puhu through got-s3e06-004
  pronunciation: { focus: 'long-vowels', words: ['dāez', 'īlva', 'vīlībagon', 'pikībagon', 'rūniapos'], lines: ['got-s3e06-004', 'got-s3e04-006', 'got-s5e04-014'] },
  letter: {
    fi: 'Kerro kolmella lauseella, keitä tai millaisia olette: minä olen…, sinä olet… ja me olemme…',
    en: 'In three sentences, say who you are or what you are like: I am…, you are… and we are…',
  },
  // Meereen, Daenerys and Hizdahr: issa, īles, īlis, istos daor and kesos daor, and two of this lesson's
  // own lines (got-s5e04-014, got-s5e04-003); every line with Peterson's recording. The Astapor scenes
  // the item named (got-s3e04-1, got-s3e05-1) have one speaker, so they cannot be played in roles
  scene: 'got-s5e04-1',
}

# Olla-verbi

Edellisellä oppitunnilla näit kolme olla-verbin preesensin muotoa: <Hv glossed>iksā</Hv> ’olet’, <Hv glossed>issa</Hv> ’on’ ja <Hv glossed>issi</Hv> ’ovat’. Tällä oppitunnilla opit koko preesensin, kaikki kuusi persoonaa, ja sen, miten olla-lause rakentuu.

Verbi <Hv glossed>sagon</Hv> ’olla’ on kielen tärkein verbi. Sillä kerrot, kuka olet ja millainen jokin on, ja sillä vastataan myös myöntävästi: valyrian ’kyllä’ on sama sana kuin ’on’. Siihen palataan alempana ja <LessonLink n={4}>seuraavalla oppitunnilla</LessonLink>.

## Kuusi persoonaa

<Paradigm lemma="sagon" filter="indicative.active.present" numbers="sg,pl" />

Taulukossa ovat olla-verbin preesensin muodot järjestyksessä minä, sinä, hän, me, te, he. Neljä niistä rakentuu vartalolle *iks-*: <Hv glossed>iksan</Hv> ’olen’, <Hv glossed>iksā</Hv> ’olet’, <Hv glossed>iksi</Hv> ’olemme’ ja <Hv glossed>iksāt</Hv> ’olette’. Kolmannen persoonan muodot ovat poikkeuksia: <Hv glossed>issa</Hv> ’on’ ja <Hv glossed>issi</Hv> ’ovat’ alkavat *iss-*. Wikin taivutustaulukko antaa ne omina muotoinaan, joten ne opetellaan sellaisinaan.

Tässä suomi auttaa. Suomenkin olla-verbi poikkeaa juuri samoissa persoonissa: *olen, olet, olemme* ja *olette* rakentuvat vartalolle *ole-*, mutta *on* ja *ovat* eivät.

**Pitkä ā.** Sinä- ja te-muodoissa on pitkä vokaali: <Hv glossed>iksā</Hv> ’olet’ ja <Hv glossed>iksāt</Hv> ’olette’. Sinä-muoto eroaa minä-muodosta <Hv glossed>iksan</Hv> ’olen’ vain lopussa. Paino on kaikissa muodoissa ensimmäisellä tavulla, joten pitkä ā on painoton. Pidä se silti pitkänä. Suomalaiselle tämä on tuttua: myös sanassa *kanaa* pitkä vokaali on painoton.

<Pronounce word="iksan" fi="olen" />

<Pronounce word="iksā" fi="olet" />

<Pronounce word="iksāt" fi="olette" />

**Kaksi s:ää.** <Hv glossed l="sagon">Issa</Hv> ’on’ ja <Hv glossed>issi</Hv> ’ovat’ äännetään kahdella s:llä, kuten suomen *kissa*: *IS-sa*, *IS-si*.

## Persoona kerrallaan

Tässä on jokaisesta persoonasta Petersonin lauseita Duolingon kurssilta ja sarjoista.

### Minä: iksan

<Example id="pt:cm-c-0020" />

<Line id="hotd-s3e08-008" />

Repliikissä on mukana myös pronomini <Hv glossed>nyke</Hv> ’minä’. Pronomini lisätään silloin, kun sitä korostetaan: minä, ei kukaan muu. Muuten se jää pois, kuten <LessonLink n={2}>oppitunnilla 2</LessonLink> opit.

### Sinä: iksā

<Example id="pt:cm-c-0022" />

’Olen ritari’ ja ’olet ritari’ eroavat toisistaan vain verbin lopussa: <Hv glossed>iksan</Hv> ’olen’, <Hv glossed>iksā</Hv> ’olet’.

<Line id="got-s3e06-004" />

### Hän: issa

<Line id="got-s5e04-014" />

Daenerysin lauseessa ’laki on laki’ on samat sanat kuin suomessa, mutta verbi on lopussa.

### Me: iksi

<Example id="pt:cm-c-0032" />

<Example id="ex-c9d2a2dffa65" />

Rhaenyra sanoo ’sinä ja minä’: <Hv glossed>ao nykē</Hv>. Sanan <Hv>nyke</Hv> loppuvokaali on pidentynyt, ja pidennys tarkoittaa ’ja’, kuten <LessonLink n={2}>oppitunnilla 2</LessonLink> opit. ’Sinä ja minä’ on yhdessä ’me’, joten verbi on <Hv glossed>iksi</Hv> ’olemme’.

### Te: iksāt

<Example id="pt:cm-c-0029" />

<Example id="ex-3155ea94aecf" />

Kummassakin lauseessa myös substantiivi on monikossa, koska puhuteltavia on monta: <Hv glossed>vali</Hv> ’miehet’ ja <Hv glossed>dāeremirossa</Hv> ’vapauttajat’.

### He: issi

<Example id="pt:cm-c-0035" />

<Line id="got-s3e08-002" />

<Hv glossed>Bisi vali</Hv> ’nämä miehet’ on subjekti, ja <Hv glossed>īlvyz zentyssy</Hv> ’meidän vieraamme’ kertoo, keitä he ovat.

## Verbi viimeisenä, adjektiivi samassa luvussa

Olla-lause rakentuu kuten muutkin lauseet: verbi on viimeisenä. Sen edellä on se, mitä joku on: substantiivi, kuten <Hv glossed>azantys</Hv> ’ritari’, tai adjektiivi, kuten <Hv glossed>kirine</Hv> ’onnellinen’. Tällainen sana on <Term id="predikatiivi">predikatiivi</Term>. Jos subjekti sanotaan, se on lauseen alussa:

<Interlinear hv="Val|i kirin|i issi." gloss="mies-NOM.PL onnellinen-NOM.PL olla-PRS.3PL" fi="Miehet ovat onnellisia." />

Jos subjektia ei sanota, verbin pääte kertoo sen:

<Interlinear hv="Kirin|i iksi." gloss="onnellinen-NOM.PL olla-PRS.1PL" fi="Olemme onnellisia." />

Kaksi asiaa mukautuu.

**Verbi mukautuu subjektiin.** ’Olemme’ on aina <Hv glossed>iksi</Hv>, oli pronomini mukana tai ei. Tuttu <Hv glossed l="sagon">issa</Hv> ’on’ sopii vain yhteen persoonaan: hän tai se. Sitä on helppo käyttää vahingossa muissakin persoonissa: ’olemme onnellisia’ ei ole *kirini issa* vaan <Hv glossed>kirini iksi</Hv>.

**Predikatiivi mukautuu subjektin lukuun.** Kun subjekti on monikossa, myös adjektiivi on monikossa: <Hv>kirine</Hv> → <Hv fi="onnelliset">kirini</Hv>. Suomessa monikon predikatiivi on yleensä partitiivissa (*olemme onnellisia*). Valyriassa partitiivia ei ole, vaan predikatiivi on nominatiivissa.

## Ei ole

Kielto opitaan kunnolla <LessonLink n={17}>oppitunnilla 17</LessonLink>. Kaksi kielteistä ilmausta kannattaa kuitenkin opetella jo nyt, koska niitä tarvitaan jatkuvasti:

- <Hv glossed>iksos daor</Hv> ’ei ole’
- <Hv glossed>ikson daor</Hv> ’en ole’

Kieltosana <Hv glossed>daor</Hv> ’ei’ tulee lauseen loppuun, ja verbi vaihtaa muotoa: kun lopussa on <Hv glossed>daor</Hv>, ’on’ ei ole <Hv glossed l="sagon">issa</Hv> vaan <Hv glossed>iksos</Hv>. Syy selviää <LessonLink n={17}>oppitunnilla 17</LessonLink>. Nyt riittää muistaa ilmaukset kokonaisina.

Tämän repliikin kuulit jo <LessonLink n={1}>oppitunnilla 1</LessonLink>:

<Line id="got-s3e04-006" />

<Line id="hotd-s1e05-003" />

Daenerysin repliikissä myönteinen ja kielteinen lause ovat peräkkäin:

<Line id="got-s7e07-005" />

Meereenin orjille pitämänsä puheen Daenerys aloittaa sanoilla <Hv glossed>Daenerys Jelmāzmo iksan</Hv> ’olen Daenerys Myrskysyntyinen’, ja hetken päästä hän sanoo <Hv glossed>jevys qrinuntys ikson daor</Hv> ’en ole vihollisenne’.

<Note>
Muiden persoonien kielteiset muodot eivät kaikki ala *iks-*. Esimerkiksi ’emme ole’ on <Hv glossed>soty daor</Hv>:

<Example id="pt:pt-c0001" />

Kaikki kuusi persoonaa opetellaan <LessonLink n={17}>oppitunnilla 17</LessonLink>.
</Note>

## Myöntävä vastaus

Myöntävä vastaus on valyriaksi <Hv l="issa">issa</Hv>, sama sana kuin <Hv glossed l="sagon">issa</Hv> ’on’: valyrian ’kyllä’ on oikeastaan ’on niin’. Kysymykseen *Onko se hyvä?* vastataan siis kuten suomessa: *On.* Muissa kysymyksissä suomi ei auta. Suomeksi toistamme kysymyksen verbin (*Tuletko? – Tulen.*), mutta valyriaksi vastaus on olla-verbin muoto. Tähän palataan <LessonLink n={4}>oppitunnilla 4</LessonLink>.

## Olla jossakin: ilagon

Kun kerrot, **missä** joku tai jokin on, valyria käyttää toista verbiä, <Hv glossed>ilagon</Hv> ’maata, sijaita’. Suomeksi se on yleensä luontevinta kääntää olla-verbillä:

<Example id="ex-1fc98cacdc51" />

<Example id="ex-f000dbb85f4e" />

Vertaa: <Hv glossed l="ilagon">ilā</Hv> ’olet (jossakin)’ ja <Hv glossed>iksā</Hv> ’olet (jotakin)’. Wikin sanakirjan mukaan <Hv>sagon</Hv> paikan yhteydessä herättää epäilyn, onko se, mistä puhutaan, enää olemassakaan: ehkä se on loppunut tai kadonnut. Paikan sijan, lokatiivin, opit <LessonLink n={19}>oppitunnilla 19</LessonLink>.

## Muut aikamuodot

Olla-verbi on epäsäännöllinen myös muissa aikamuodoissa. Ne opitaan myöhemmin, mutta sarjoissa ne tulevat vastaan jo nyt. Tässä niiden tuntomerkit.

### Futuuri: kes-

Futuurin vartalo on *kes-*: <Hv glossed>kesan</Hv> ’tulen olemaan’, <Hv glossed l="sagon">kessa</Hv> ’tulee olemaan’. Suomessa futuuria ei ole, joten käännöksessä on usein preesens tai *tulla*-verbi:

<Example id="ex-238a7b80dcce" />

<Line id="hotd-s1e01-021" />

Rhaenyran repliikissä on kieltosana <Hv glossed>dōrī</Hv> ’ei koskaan’, mutta verbi on silti tavallisessa muodossaan. Kieltosanoista vain <Hv>daor</Hv> vaihtaa verbin muodon.

Futuuri opitaan <LessonLink n={26}>oppitunnilla 26</LessonLink>.

### Perfekti: ist-

Perfektin vartalo on *ist-*: <Hv glossed l="sagon">istan</Hv> ’olin, olen ollut’, <Hv glossed l="sagon">istas</Hv> ’oli, on ollut’. Perfekti kertoo, että asia on päättynyt.

<Example id="ex-083c392857e8" />

Verbin <Hv glossed>jagon</Hv> ’mennä’ perfektin muodot ovat samat. Lauseen muut sanat kertovat, kumpi on kyseessä. Alla <Hv glossed>Yunkaihot</Hv> ’Yunkaihin’ kertoo menemisestä:

<Example id="ex-677f3283fdca" />

Perfekti opitaan <LessonLink n={22}>oppitunnilla 22</LessonLink>.

### Imperfekti: īl-

Imperfektin vartalo on *īl-*: <Hv glossed l="sagon">īlen</Hv> ’olin’, <Hv glossed l="sagon">īles</Hv> ’oli’. Valyrian imperfekti ei ole suomen mennyt aika (koulukieliopin imperfekti): se kertoo, mikä oli käynnissä tai voimassa.

<Line id="got-s5e04-003" />

Myös imperfektin muodot ovat samat kuin verbillä <Hv>jagon</Hv>: <Hv glossed>īles</Hv> voi olla ’oli’ tai ’oli menossa’. Imperfekti opitaan <LessonLink n={34}>oppitunnilla 34</LessonLink>.

### Mennyt toistuva: istin

Mennyt toistuva <Hv glossed l="sagon" k="past-habitual.1.sg">istin</Hv> on ’olin (ennen, kerran)’. Tällä oppitunnilla riittää, että tunnistat sen. Se oli jo Melisandren repliikissä <LessonLink n={2}>oppitunnilla 2</LessonLink>: <Hv glossed>Dohaeriros istin</Hv> ’olin kerran orja’.

<Line id="got-s5e03-004" />

Mennyt toistuva opitaan <LessonLink n={41}>oppitunnilla 41</LessonLink>.

## Harjoitellaan

Nämä lauseet on koottu tämän oppitunnin säännöillä ja taivutustaulukon muodoilla. Ne eivät ole Petersonin lauseita. Jokaisessa on eri persoona: lue ne ääneen ja pidä pitkä ā pitkänä.

<Practice hv="Kirine iksan." fi="Olen onnellinen." />

<Practice hv="Sȳz vala iksā." fi="Olet hyvä mies." />

<Practice hv="Vala kirine issa." fi="Mies on onnellinen." />

<Practice hv="Dāeri iksi." fi="Olemme vapaita." />

<Practice hv="Kirini iksāt." fi="Olette onnellisia." />

<Practice hv="Dāeri issi." fi="He ovat vapaita." />

Vertaa neljättä ja viimeistä lausetta: adjektiivi <Hv fi="vapaat">dāeri</Hv> on molemmissa sama, koska subjekti on kummassakin monikossa. Adjektiivista ei näe persoonaa. Vain verbi kertoo, keistä puhutaan: <Hv glossed>iksi</Hv> ’olemme’, <Hv glossed>issi</Hv> ’ovat’.

**Muotojen kuuntelukortit.** Tämän oppitunnin jälkeen päivän korteissa voi tulla vastaan myös tuttujen verbien preesensmuotoja kuuntelukortteina. Sano, mitä verbi tarkoittaa ja kenestä puhutaan, esimerkiksi ”preesens, me”.

## Sanat

<Vocab />

<Check />
